import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import {
  DEFAULT_THEME,
  FONT_SIZE_VALUES,
  THEME_VALUES,
  applyTheme,
  chooseFontSize,
  chooseTheme,
  readFontSizePreference,
  readThemePreference,
  watchSystemScheme,
  type FontSizePreference,
  type ThemePreference,
} from '../stores/session.js';
import { V4FormRow, V4Group, V4Segmented } from './ux-v4.js';
import {
  canPromptInstall,
  isIos,
  isStandalone,
  onInstallAvailabilityChange,
  promptInstall,
} from '../lib/pwa.js';

/**
 * PREPINACE VZHLEDU — JEDNO MISTO (krok UXV4p bod 3).
 *
 * Zadani: „Volba Velikost pisma a Vzhled PRIMO V MENU PROFILU (jednim
 * klepnutim z kazde obrazovky) i v Nastaveni." Dve kopie tehoz prepinace by
 * se rozesly (jedna by zapomnela ulozit na server, druha by mela jiny stupen),
 * proto je tady a Nastaveni i nabidka profilu ji jen vykresluji.
 *
 * Stav se cte z `localStorage` (tam ho uz pri startu srovnal
 * `applyServerUiPreferences` s tim, co ma uzivatel na serveru) a zmena jde
 * pres `chooseFontSize`/`chooseTheme` — atribut na `<html>` hned, kes
 * prohlizece, server.
 */

const FONT_SIZE_LABEL_KEY: Record<FontSizePreference, MessageKey> = {
  small: 'webAdmin.settings.fontSizeSmall',
  normal: 'webAdmin.settings.fontSizeNormal',
  large: 'webAdmin.settings.fontSizeLarge',
  largest: 'webAdmin.settings.fontSizeLargest',
};

const THEME_LABEL_KEY: Record<ThemePreference, MessageKey> = {
  light: 'webAdmin.settings.themeLight',
  dark: 'webAdmin.settings.themeDark',
  system: 'webAdmin.settings.themeSystem',
};

/** Spolecny stav obou prepinacu — at je vykresli kdokoli, rikaji totez. */
function useAppearance(): {
  readonly fontSize: FontSizePreference;
  readonly theme: ThemePreference;
  readonly setFontSize: (size: FontSizePreference) => void;
  readonly setTheme: (theme: ThemePreference) => void;
} {
  const [fontSize, setFontSizeState] = React.useState<FontSizePreference>(
    () => readFontSizePreference() ?? 'normal',
  );
  // Krok WEBN3 (zadani bod 1): vychozi je SVETLY, ne „podle systému" —
  // `DEFAULT_THEME` je jediny zdroj, ktery zna i server a `main.tsx`.
  const [theme, setThemeState] = React.useState<ThemePreference>(
    () => readThemePreference() ?? DEFAULT_THEME,
  );

  // „Podle systému" musi reagovat i na zmenu systemoveho nastaveni za behu.
  React.useEffect(() => {
    if (theme !== 'system') return;
    return watchSystemScheme(() => {
      applyTheme('system');
    });
  }, [theme]);

  return {
    fontSize,
    theme,
    setFontSize: (size) => {
      chooseFontSize(size);
      setFontSizeState(size);
    },
    setTheme: (next) => {
      chooseTheme(next);
      setThemeState(next);
    },
  };
}

const LOCALE = 'cs' as const;

/**
 * Prepinace v NABIDCE PROFILU — jedno klepnuti z kazde obrazovky (zadani
 * bod 3). Zamerne BEZ `DropdownMenu.Item`: polozka nabidky se po kliknuti
 * zavira, a prepinac, ktery po kazdem kliku zmizi, se neda vyzkouset.
 */
export function AppearanceMenuSection(): React.JSX.Element {
  const { fontSize, theme, setFontSize, setTheme } = useAppearance();
  return (
    <div className="rh-a2-menu__appearance" data-testid="menu-appearance">
      <div className="rh-a2-menu__appearance-row">
        <span className="rh-a2-meta">{t('webAdmin.settings.fontSizeRowLabel', { locale: LOCALE })}</span>
        <V4Segmented
          value={fontSize}
          onChange={setFontSize}
          label={t('webAdmin.settings.fontSizeLabel', { locale: LOCALE })}
          testId="menu-font-size"
          options={FONT_SIZE_VALUES.map((value) => ({
            value,
            label: t(FONT_SIZE_LABEL_KEY[value], { locale: LOCALE }),
          }))}
        />
      </div>
      <div className="rh-a2-menu__appearance-row">
        <span className="rh-a2-meta">{t('webAdmin.settings.themeLabel', { locale: LOCALE })}</span>
        <V4Segmented
          value={theme}
          onChange={setTheme}
          label={t('webAdmin.settings.themeLabel', { locale: LOCALE })}
          testId="menu-theme"
          options={THEME_VALUES.map((value) => ({
            value,
            label: t(THEME_LABEL_KEY[value], { locale: LOCALE }),
          }))}
        />
      </div>
    </div>
  );
}

/**
 * Krok MOB1 bod 5 — INSTALACE NA PLOCHU v Nastaveni → Vzhled.
 *
 * Ukazuje se JEN kdyz ma co nabidnout: v rezimu aplikace (uz nainstalovano)
 * je misto nej veta, ze hotovo. Android dostane tlacitko (odchycena nabidka
 * `beforeinstallprompt`, viz `lib/pwa.ts`), iOS kratky navod — Safari zadnou
 * nabidku nema a tlacitko, ktere nic neudela, by bylo horsi nez veta.
 *
 * Pro nativni obal (Capacitor) je to bez prace: tam je `isStandalone()` true,
 * takze se sekce neukaze vubec.
 */
function InstallSection(): React.JSX.Element {
  const [installed, setInstalled] = React.useState(() => isStandalone());
  const [canPrompt, setCanPrompt] = React.useState(() => canPromptInstall());

  React.useEffect(
    () =>
      onInstallAvailabilityChange(() => {
        setCanPrompt(canPromptInstall());
        setInstalled(isStandalone());
      }),
    [],
  );

  if (installed) {
    return (
      <V4FormRow label={t('webAdmin.settings.installRowLabel', { locale: LOCALE })}>
        <span className="rh-a2-meta" data-testid="install-done">
          {t('webAdmin.settings.installDone', { locale: LOCALE })}
        </span>
      </V4FormRow>
    );
  }

  return (
    <V4FormRow
      label={t('webAdmin.settings.installRowLabel', { locale: LOCALE })}
      hint={t('webAdmin.settings.installHint', { locale: LOCALE })}
    >
      {canPrompt ? (
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          data-testid="install-app"
          onClick={() => { void promptInstall(); }}
        >
          {t('webAdmin.settings.installButton', { locale: LOCALE })}
        </button>
      ) : (
        /*
          Krok UXD3b (nalez K1): mimo iPhone/iPad tu stala TATAZ veta jako
          v napovede radku, takze „Makléřov poběží jako aplikace…" byla na
          obrazovce dvakrat za sebou a nerekla nic noveho. Kdyz prohlizec
          instalaci nenabizi, patri sem DALSI KROK, ne opakovani.
        */
        <span className="rh-a2-meta" data-testid="install-ios-hint">
          {isIos()
            ? t('webAdmin.settings.installIosHint', { locale: LOCALE })
            : t('webAdmin.settings.installUnavailableHint', { locale: LOCALE })}
        </span>
      )}
    </V4FormRow>
  );
}

/** Tytez prepinace v Nastaveni → Vzhled (sekce s popisky a vysvetlenim). */
export function AppearanceSection(): React.JSX.Element {
  const { fontSize, theme, setFontSize, setTheme } = useAppearance();
  return (
    <div style={{ maxWidth: 640, marginBottom: 16 }}>
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.appearanceTitle', { locale: LOCALE })}</h2>
      <V4Group testId="nastaveni-appearance-card">
        {/* Krok UXF4 bod 5 (nalez N8): nazev nad cipy, cipy pres sirku karty. */}
        <V4FormRow
          stack
          label={t('webAdmin.settings.themeLabel', { locale: LOCALE })}
          hint={t('webAdmin.settings.themeHint', { locale: LOCALE })}
        >
          <V4Segmented
            value={theme}
            onChange={setTheme}
            label={t('webAdmin.settings.themeLabel', { locale: LOCALE })}
            testId="theme-switch"
            options={THEME_VALUES.map((value) => ({
              value,
              label: t(THEME_LABEL_KEY[value], { locale: LOCALE }),
            }))}
          />
        </V4FormRow>
        <V4FormRow
          stack
          label={t('webAdmin.settings.fontSizeRowLabel', { locale: LOCALE })}
          hint={t('webAdmin.settings.fontSizeHint', { locale: LOCALE })}
        >
          <V4Segmented
            value={fontSize}
            onChange={setFontSize}
            label={t('webAdmin.settings.fontSizeLabel', { locale: LOCALE })}
            testId="font-size"
            options={FONT_SIZE_VALUES.map((value) => ({
              value,
              label: t(FONT_SIZE_LABEL_KEY[value], { locale: LOCALE }),
            }))}
          />
        </V4FormRow>
        <InstallSection />
      </V4Group>
    </div>
  );
}
